// utils/ParagraphUtils.js - Pure functions for paragraph operations
import speakerMapper from 'mappers/SpeakerMapper';
import SentenceBuilder from './SentenceBuilder';
import GetCodes from './CodesFromParagraph';
import _ from 'underscore';

// Create a simple paragraph data structure (immutable)
export const createParagraph = (text, start, paragraphIndex) => {
  const end = start + text.length;
  const timeAndSpeaker = speakerMapper(text);
  
  const paragraph = {
    paragraphIndex,
    text,
    start,
    end,
    speaker: timeAndSpeaker?.name || null,
    time: timeAndSpeaker?.time || null,
    introText: timeAndSpeaker?.introText || null,
    bodyText: timeAndSpeaker?.bodyText ?? text
  };
  
  return paragraph;
};

// Build paragraphs from transcript text
export const buildParagraphs = (transcriptText) => {
  if (!transcriptText) return [];
  
  const paragraphs = [];
  const lines = transcriptText.split(/\r?\n/);
  let currentStart = 0;
  
  lines.forEach((line, index) => {
    if (line.trim()) { // Only create paragraphs for non-empty lines
      const paragraph = createParagraph(line, currentStart, index);
      paragraphs.push(paragraph);
    }
    currentStart += line.length + 1; // +1 for newline character
  });
  
  return paragraphs;
};

// Build sentences for a paragraph with excerpts
const buildSentencesForParagraph = (paragraph, excerpts, extraBoundaries) => {
  const relevantExcerpts = excerpts.filter(excerpt =>
    !(excerpt.end <= paragraph.start || excerpt.start >= paragraph.end)
  );

  const sentenceBuilder = new SentenceBuilder(
    paragraph.end,
    paragraph.start,
    paragraph.introText,
    paragraph.bodyText
  );

  return sentenceBuilder.constructSentences(relevantExcerpts, extraBoundaries);
};

// Get codes for a paragraph based on excerpts
const getCodesForParagraph = (paragraph, excerpts, selectedExcerptId, isSelectInsideParagraph) => {
  const relevantExcerpts = excerpts.filter(excerpt => 
    !(excerpt.end <= paragraph.start || excerpt.start >= paragraph.end)
  );
  
  return GetCodes(relevantExcerpts, selectedExcerptId, isSelectInsideParagraph);
};

// Pure function to create a coded paragraph.
// extraBoundaries (optional): absolute offsets at which to additionally split
// rendered spans — see SentenceBuilder#constructSentences. Omitted everywhere
// except the comparison page, where it carries the diff-segment edges.
export const getCodedParagraph = (
  paragraph,
  excerpts,
  speakerColorHash,
  select,
  primeCode,
  selectedExcerptId,
  extraBoundaries
) => {
  const isSelectInsideParagraph = !!select &&
    !(select.end <= paragraph.start || select.start >= paragraph.end);

  const sentences = buildSentencesForParagraph(paragraph, excerpts, extraBoundaries);
  const codes = getCodesForParagraph(paragraph, excerpts, selectedExcerptId, isSelectInsideParagraph);
  
  return {
    start: paragraph.start,
    end: paragraph.end,
    text: paragraph.bodyText,
    speaker: paragraph.speaker,
    color: (speakerColorHash || {})[paragraph.speaker] || null,
    time: paragraph.time,
    codes,
    index: paragraph.paragraphIndex,
    sentences: sentences.map(sentence =>
      sentence.getCodedFormat(primeCode, select, excerpts)
    )
  };
};

// Create speaker color mapping
export const createSpeakerColorHash = (paragraphs, colors) => {
  const speakers = _.uniq(paragraphs
    .map(paragraph => paragraph.speaker)
    .filter(speaker => !!speaker)
  );

  const colorHash = {};
  speakers.forEach((speaker, index) => {
    colorHash[speaker] = colors[index % colors.length];
  });
  
  return colorHash;
};

// Get paragraph that contains a specific character location
export const findParagraphByLocation = (paragraphs, loc) => {
  return paragraphs.find(paragraph => 
    paragraph.start <= loc && loc <= paragraph.end
  );
};

// Get paragraph index by location
export const getParagraphIndexByLocation = (paragraphs, loc) => {
  const index = paragraphs.findIndex(paragraph => 
    paragraph.start <= loc && loc <= paragraph.end
  );
  return index >= 0 ? index : paragraphs.length - 1;
};